// ============================================================
// WINNERSAURUS — useAcademyProgress Hook
//
// Tracks which academy articles a logged-in user has completed.
// Reads from and writes to the academy_progress table in Supabase.
// Progress is stored per-user and persists across devices.
// ============================================================

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';
import { academyArticles } from '../data/blogData';

const TOTAL_ARTICLES = academyArticles.length; // 8

export default function useAcademyProgress() {
  const { user } = useAuth();
  const [completedSlugs, setCompletedSlugs] = useState(new Set());
  const [loading, setLoading] = useState(true);

  // Load progress from Supabase on mount
  useEffect(() => {
    if (!user) {
      setCompletedSlugs(new Set());
      setLoading(false);
      return;
    }

    async function fetchProgress() {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('academy_progress')
          .select('article_slug')
          .eq('user_id', user.id);

        if (error) throw error;
        const slugs = new Set((data || []).map(r => r.article_slug));
        setCompletedSlugs(slugs);
      } catch (err) {
        console.error('Failed to load academy progress:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchProgress();
  }, [user]);

  // Mark an article as complete
  const markComplete = useCallback(async (slug) => {
    if (!user || completedSlugs.has(slug)) return;

    // Optimistic update
    setCompletedSlugs(prev => new Set([...prev, slug]));

    try {
      const { error } = await supabase
        .from('academy_progress')
        .upsert({ user_id: user.id, article_slug: slug }, { onConflict: 'user_id,article_slug' });

      if (error) {
        // Rollback optimistic update
        setCompletedSlugs(prev => {
          const next = new Set(prev);
          next.delete(slug);
          return next;
        });
        console.error('Failed to save progress:', error);
      }
    } catch (err) {
      console.error('Failed to save progress:', err);
    }
  }, [user, completedSlugs]);

  // Derived values
  const completedCount    = completedSlugs.size;
  const progressPercent   = Math.round((completedCount / TOTAL_ARTICLES) * 100);
  const isComplete        = completedCount === TOTAL_ARTICLES;
  const isArticleComplete = (slug) => completedSlugs.has(slug);

  return {
    completedSlugs,
    completedCount,
    progressPercent,
    isComplete,
    isArticleComplete,
    markComplete,
    loading,
    totalArticles: TOTAL_ARTICLES,
  };
}
